import { useEffect, useState } from "react";
import { Alert, Box, Button, Modal, Stack, Text } from "@mantine/core";
import ConfirmationModal from "../../../maintenance/components/platformDetail/ConfirmationModal";
import PlatformStepper from "./PlatformStepper";
import { Platform, PlatformUnit } from "./types";
import {
  MAINTENANCE_STEPS,
  maintenanceStepIndex,
  mapPlatformStatus,
  statusPill,
} from "./status";

export type PlatformActionKind =
  | "request"
  | "return"
  | "reset"
  | "moveToMaintenance"
  | "returnFromMaintenance";
export interface PlatformPending {
  action: PlatformActionKind;
  unitId?: number;
}

interface PlatformModalProps {
  opened: boolean;
  onClose: () => void;
  platform: Platform;
  onRequest: (unit: PlatformUnit) => void;
  onReturn: (unit: PlatformUnit) => void;
  onReset: () => void;
  pending: PlatformPending | null;
  errorText?: string | null;
  onDismissError?: () => void;
  onMoveToMaintenance?: () => void;
  onReturnFromMaintenance?: () => void;
}

function StatusPill({ label }: { label: string }) {
  const p = statusPill(label);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: p.bg,
        color: p.text,
        border: `1px solid ${p.border}`,
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 10px",
        borderRadius: 999,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </span>
  );
}

// While a platform is busy the modal shows a heading + subtext describing what
// it is doing. `busyPhase` collapses the raw/mapped status into one of these
// phases and BUSY_PHASE_COPY holds the wording — one place to read and edit it.
type BusyPhase =
  | "fault"
  | "inMaintenance"
  | "movingToMaintenance"
  | "returningFromMaintenance"
  | "returning"
  | "atDock"
  | "waiting";

function busyPhase(rawStatus: string): BusyPhase {
  // Two raw VLC states share the "Maintenance" label but need distinct copy,
  // so match them before falling back to the mapped display label.
  if (rawStatus === "InMaintenance") return "inMaintenance";
  if (rawStatus === "ReturnFromMaintenanceRequested") return "returningFromMaintenance";

  switch (mapPlatformStatus(rawStatus)) {
    case "Fault":
      return "fault";
    case "Maintenance": // remaining: ToMaintenanceRequested / MovingToMaintenance
      return "movingToMaintenance";
    case "Returning":
      return "returning";
    case "At Dock":
      return "atDock";
    default:
      return "waiting";
  }
}

const BUSY_PHASE_COPY: Record<BusyPhase, { heading: string; subtext: string }> = {
  fault: {
    heading: "Platform fault",
    subtext: "The platform reported a fault. Reset the status, or contact support if it persists.",
  },
  inMaintenance: {
    heading: "Platform in maintenance",
    subtext: "The platform is in the maintenance area. Return it to storage when the work is done.",
  },
  movingToMaintenance: {
    heading: "Moving to maintenance…",
    subtext: "The platform has been called to the maintenance area. Please wait for it to arrive.",
  },
  returningFromMaintenance: {
    heading: "Returning from maintenance…",
    subtext: "The platform is on its way back to storage.",
  },
  returning: {
    heading: "Returning unit to storage…",
    subtext: "The unit is on its way back to storage.",
  },
  atDock: {
    heading: "Unit has arrived at the dock",
    subtext: "The platform has reached the dock.",
  },
  waiting: {
    heading: "Waiting for platform arrival…",
    subtext: "The unit has been requested. Please wait for the platform to reach the dock.",
  },
};

export default function PlatformModal({
  opened,
  onClose,
  platform,
  onRequest,
  onReturn,
  onReset,
  pending,
  errorText,
  onDismissError,
  onMoveToMaintenance,
  onReturnFromMaintenance,
}: PlatformModalProps) {
  const activeLabel = mapPlatformStatus(platform.status);
  const isAvailable = activeLabel === "Available";
  const busy = platform.currentUnitId != null || !isAvailable;
  const phase = busyPhase(platform.status);
  const { heading, subtext } = BUSY_PHASE_COPY[phase];

  const isAtDock = phase === "atDock";
  const isMaintenance =
    phase === "inMaintenance" ||
    phase === "movingToMaintenance" ||
    phase === "returningFromMaintenance";

  const canMoveToMaintenance = onMoveToMaintenance != null;
  const canReturnFromMaintenance = onReturnFromMaintenance != null && phase === "inMaintenance";

  const activeUnit = platform.units.find((u) => u.unitId === platform.currentUnitId) ?? null;
  const anyPending = pending !== null;
  const title = `Platform ${platform.platformNumber} Details`;
  const activeUnitNumber = activeUnit?.unitNumber ?? platform.currentUnitNumber ?? "—";
  const hasActiveUnit = activeUnit != null || platform.currentUnitNumber != null;

  const [confirmingReset, setConfirmingReset] = useState(false);
  const resetPending = pending?.action === "reset";

  useEffect(() => {
    if (!opened) setConfirmingReset(false);
  }, [opened]);

  const resetButton = (
    <Button
      variant="default"
      fullWidth
      onClick={() => setConfirmingReset(true)}
      loading={resetPending}
      disabled={anyPending && !resetPending}
    >
      Reset Status
    </Button>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={600} fz="xl" c="primary.6">
          {title}
        </Text>
      }
      centered
      radius="lg"
      size={760}
    >
      {errorText && (
        <Alert color="red" radius="md" variant="light" mb="md" withCloseButton onClose={onDismissError}>
          {errorText}
        </Alert>
      )}

      {busy ? (
        <Stack align="center" gap="md" py="md">
          <Text fw={600} fz="lg" c="primary.6">
            {heading}
          </Text>
          <Text c="dimmed" ta="center">
            {subtext}
          </Text>

          <Stack align="center" gap={6} my="sm">
            {hasActiveUnit && (
              <>
                <Text fz="sm" c="dimmed">
                  Requested Unit
                </Text>
                <Text fw={700} fz="md">
                  Unit {activeUnitNumber}
                </Text>
              </>
            )}
            <StatusPill label={activeLabel} />
          </Stack>

          <Box w="70%" pb={28} pt={8}>
            {isMaintenance ? (
              <PlatformStepper
                status={platform.status}
                steps={MAINTENANCE_STEPS}
                activeIndex={maintenanceStepIndex(platform.status)}
              />
            ) : (
              <PlatformStepper status={activeLabel} />
            )}
          </Box>

          {isAtDock && activeUnit ? (
            <Stack align="center" gap="sm">
              <Text c="green.7" fw={600}>
                Platform has arrived!
              </Text>
              <Button
                color="red"
                radius="md"
                size="md"
                onClick={() => onReturn(activeUnit)}
                loading={pending?.action === "return"}
                disabled={anyPending && pending?.action !== "return"}
              >
                Return Unit to Storage
              </Button>
            </Stack>
          ) : isMaintenance ? (
            canReturnFromMaintenance ? (
              <Stack align="center" gap="sm">
                <Button
                  color="teal"
                  radius="md"
                  size="md"
                  onClick={onReturnFromMaintenance}
                  loading={pending?.action === "returnFromMaintenance"}
                  disabled={anyPending && pending?.action !== "returnFromMaintenance"}
                >
                  Return from Maintenance
                </Button>
              </Stack>
            ) : null
          ) : (
            <Text c="dimmed" fz="sm">
              Platform is {activeLabel.toLowerCase()}…
            </Text>
          )}

          <Box w="50%" mt="xl">
            {resetButton}
          </Box>
        </Stack>
      ) : (
        <Stack gap="lg">
          <Box>
            <Text fz="sm" fw={700} c="dimmed" tt="uppercase" mb="xs">
              Assigned Units ({platform.units.length})
            </Text>

            {platform.units.length === 0 ? (
              <Text c="dimmed" fz="sm" fs="italic">
                No units assigned yet.
              </Text>
            ) : (
              <Stack gap={0}>
                {platform.units.map((unit, i) => (
                  <Box
                    key={unit.unitId}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "120px minmax(0, 1fr) 140px",
                      gap: 12,
                      alignItems: "center",
                      padding: "10px 4px",
                      borderBottom: i < platform.units.length - 1 ? "1px solid #e2e8f0" : undefined,
                    }}
                  >
                    <Text fw={700} fz={14}>
                      Unit {unit.unitNumber}
                    </Text>
                    <Box style={{ minWidth: 0 }}>
                      {unit.owner ? (
                        <>
                          <Text fw={600} fz={13} truncate>
                            {unit.owner.name || "—"}
                          </Text>
                          {unit.owner.email && (
                            <Text fz={12} c="dimmed" truncate>
                              {unit.owner.email}
                            </Text>
                          )}
                        </>
                      ) : (
                        <Text fz={13} c="dimmed" fs="italic">
                          Not rented
                        </Text>
                      )}
                    </Box>
                    <Box style={{ textAlign: "right" }}>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => onRequest(unit)}
                        loading={pending?.action === "request" && pending.unitId === unit.unitId}
                        disabled={anyPending && !(pending?.action === "request" && pending.unitId === unit.unitId)}
                      >
                        Request to Dock
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          {canMoveToMaintenance && (
            <Box
              style={{
                display: "flex",
                justifyContent: "flex-end",
                borderTop: "1px solid #e2e8f0",
                paddingTop: 16,
              }}
            >
              <Button
                variant="light"
                color="yellow"
                radius="md"
                onClick={onMoveToMaintenance}
                loading={pending?.action === "moveToMaintenance"}
                disabled={anyPending && pending?.action !== "moveToMaintenance"}
              >
                Request to Maintenance
              </Button>
            </Box>
          )}
        </Stack>
      )}

      <ConfirmationModal
        isOpen={confirmingReset}
        onRequestClose={() => setConfirmingReset(false)}
        onConfirm={() => {
          onReset();
          setConfirmingReset(false);
        }}
        type="reset"
        isProcessing={resetPending}
      />
    </Modal>
  );
}
